import { BrandhubResource } from '@brandhub/types/brandhub';
import { InputField } from '@components/form/input-field/InputField';
import { TextAreaField } from '@components/form/text-area/TextAreaField';
import { InputCharsCount } from '@components/input-chars-counter/InputCharsCount';
import { TooltipInfo } from '@components/tooltip-info/TooltipInfo';
import { BrandhubResourcesModal } from '@mailings/components/common/brandhub-resources-modal/BrandhubResourcesModal';
import { ResearchFormDisabledFileUpload } from '@mailings/components/creators/personalized-mailing-creator-wizard/components/mailing-objectives/forms/file-upload/ResearchFormDisabledFileUpload';
import { ResearchFormFileUpload } from '@mailings/components/creators/personalized-mailing-creator-wizard/components/mailing-objectives/forms/file-upload/ResearchFormFileUpload';
import { useObjectivesFormFileUploadStatus } from '@mailings/components/creators/personalized-mailing-creator-wizard/hooks/useObjectivesFormFileUploadStatus';
import { ResearchFormType } from '@mailings/components/creators/personalized-mailing-creator-wizard/types/mailingObjectivesForm.types';
import { isSerializedFormFile } from '@mailings/components/creators/personalized-mailing-creator-wizard/utils/fileUploadUtils';
import { FIELD_CONFIGS } from '@mailings/components/creators/personalized-mailing-creator-wizard/validations/getBaseObjectivesFormValidationSchema.validations';
import { Text } from '@semcore/ui/typography';
import { useFileUpload } from '@src/hooks/useFileUpload';
import { useState, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ResearchFormDisabledStoryLinkInput } from './file-upload/ResearchFormDisabledStoryLinkInput';

export const ResearchFormFields = () => {
  const { t } = useTranslation(['mailings']);

  const [isResourcesModalVisible, setIsResourcesModalVisible] = useState(false);

  const { register, control, setValue } = useFormContext<ResearchFormType>();

  const { uploadStatus, setUploadStatus, setFileUploadRestrictionsErrors } =
    useObjectivesFormFileUploadStatus();
  const { file, handleFileSelection, handleRemoveFile } = useFileUpload({
    onUploadStatusChange: setUploadStatus,
  });

  const storyDocumentFile = useWatch({ control, name: 'storyDocumentFile' });
  const hasSavedFile = isSerializedFormFile(storyDocumentFile);

  useEffect(() => {
    if (!hasSavedFile) {
      setValue('storyDocumentFile', file, { shouldDirty: true });
    }
  }, [file, setValue, hasSavedFile]);

  const researchFindings = useWatch({ control, name: 'researchFindings' }) || '';

  const researchDescription = useWatch({ control, name: 'researchDescription' }) || '';

  const researchSources = useWatch({ control, name: 'researchSources' }) || '';

  const storyLink = useWatch({ control, name: 'storyLink' }) ?? '';

  const resourceId = useWatch({
    control,
    name: 'brandHubResourceId',
    defaultValue: undefined,
  });

  const handleSelectFromBrandHub = () => {
    setIsResourcesModalVisible(true);
  };

  const handleAddResource = (resource: BrandhubResource) => {
    setValue('brandHubResourceId', resource.id);
    setValue('brandHubResourceName', resource.name);
    setIsResourcesModalVisible(false);
  };

  const handleResourceRemove = () => {
    if (storyDocumentFile) {
      setValue('storyDocumentFile', null, { shouldDirty: true });
    }

    setValue('brandHubResourceId', undefined, { shouldDirty: true });
    setValue('brandHubResourceName', undefined);
    // When resource is URL, BE adds storyLink to input
    setValue('storyLink', '');
  };

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Text
            htmlFor="research-link-input"
            tag="label">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchLink.label',
            )}
          </Text>
          <TooltipInfo
            ariaLabel={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchLink.ariaLabel',
            )}
            placement="right">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchLink.tooltip',
            )}
          </TooltipInfo>
        </div>
        {Boolean(file) || hasSavedFile || Boolean(resourceId) ? (
          <ResearchFormDisabledStoryLinkInput />
        ) : (
          <InputField
            autoComplete="off"
            data-test="research-link-input"
            id="research-link-input"
            placeholder={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchLink.placeholder',
            )}
            size="l"
            {...register('storyLink')}
          />
        )}

        {storyLink && !resourceId ? (
          <ResearchFormDisabledFileUpload />
        ) : (
          <ResearchFormFileUpload
            file={file}
            setFileUploadRestrictionsErrors={setFileUploadRestrictionsErrors}
            setUploadStatus={setUploadStatus}
            uploadStatus={uploadStatus}
            onFileRemove={handleRemoveFile}
            onFileSelect={handleFileSelection}
            onResourceRemove={handleResourceRemove}
            onSelectFromBrandHub={handleSelectFromBrandHub}
          />
        )}
        <BrandhubResourcesModal
          isVisible={isResourcesModalVisible}
          onAddResource={handleAddResource}
          onClose={() => {
            setIsResourcesModalVisible(false);
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Text
            htmlFor="research-topic-textarea"
            tag="label">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchTopic.label',
            )}
          </Text>
          <TooltipInfo
            ariaLabel={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchTopic.ariaLabel',
            )}
            placement="right">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchTopic.tooltip',
            )}
          </TooltipInfo>
          <InputCharsCount
            count={researchFindings.length}
            maxCount={FIELD_CONFIGS.RESEARCH.RESEARCH_FINDINGS}
            size="l"
          />
        </div>
        <TextAreaField
          data-test="research-topic-textarea"
          id="research-topic-textarea"
          maxRows={6}
          minRows={4}
          placeholder={t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchTopic.placeholder',
          )}
          size="l"
          {...register('researchFindings')}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Text
            htmlFor="research-overview-textarea"
            tag="label">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchOverview.label',
            )}
          </Text>
          <TooltipInfo
            ariaLabel={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchOverview.ariaLabel',
            )}
            placement="right">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchOverview.tooltip',
            )}
          </TooltipInfo>
          <InputCharsCount
            count={researchDescription.length}
            maxCount={FIELD_CONFIGS.RESEARCH.RESEARCH_DESCRIPTION}
            size="l"
          />
        </div>
        <TextAreaField
          data-test="research-overview-textarea"
          id="research-overview-textarea"
          maxRows={6}
          minRows={4}
          placeholder={t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchOverview.placeholder',
          )}
          size="l"
          {...register('researchDescription')}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Text
            htmlFor="methodology-and-credibility-textarea"
            tag="label">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.methodologyAndCredibility.label',
            )}
          </Text>
          <TooltipInfo
            ariaLabel={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.methodologyAndCredibility.ariaLabel',
            )}
            placement="right">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.methodologyAndCredibility.tooltip',
            )}
          </TooltipInfo>
          <InputCharsCount
            count={researchSources.length}
            maxCount={FIELD_CONFIGS.RESEARCH.RESEARCH_SOURCES}
            size="l"
          />
        </div>
        <TextAreaField
          data-test="methodology-and-credibility-textarea"
          id="methodology-and-credibility-textarea"
          maxRows={6}
          minRows={4}
          placeholder={t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.research.methodologyAndCredibility.placeholder',
          )}
          size="l"
          {...register('researchSources')}
        />
      </div>
    </div>
  );
};
